// =============================================================================
// ORDERS DATA LAYER - SUPABASE
// Provides Supabase-backed order operations with localStorage fallback
// =============================================================================

import { logger } from '@/utils/logger'
import { supabase, requireTenantId } from '../../lib/supabase'
import { transformOrderFromSupabase } from '../../services/OrderTransformService'

const log = logger.module('SupabaseOrders')

// =============================================================================
// HELPER: Strip base64 image data to prevent PostgreSQL timeout (error 57014)
// Base64 images can be megabytes large and cause statement timeouts
// =============================================================================
function stripImageData(obj: any): any {
  if (!obj) return obj
  if (Array.isArray(obj)) {
    return obj.map(item => stripImageData(item))
  }
  if (typeof obj === 'object') {
    const stripped: any = {}
    for (const [key, value] of Object.entries(obj)) {
      // Skip keys that typically contain base64 image data
      if (key === 'imageData' || key === 'image' || key === 'dataUrl') {
        continue
      }
      // Check if value looks like base64 image data
      if (typeof value === 'string' && value.startsWith('data:image/')) {
        continue
      }
      // Strip renders array completely - not needed in Supabase
      if (key === 'renders' && Array.isArray(value)) {
        stripped[key] = null
        continue
      }
      // Strip floor_plan_capture - not needed in Supabase
      if (key === 'floor_plan_capture' && typeof value === 'string' && value.length > 1000) {
        stripped[key] = null
        continue
      }
      stripped[key] = stripImageData(value)
    }
    return stripped
  }
  return obj
}

// Type for Supabase error
interface SupabaseError {
  message: string
  details?: string
  code?: string
  status?: number
}

// Client data
interface ClientData {
  [key: string]: any
}

// Configuration data
interface ConfigData {
  [key: string]: any
}

// Add-ons
interface AddOn {
  [key: string]: any
}

// Bed data
interface Bed {
  [key: string]: any
}

// Pricing data
interface PricingData {
  [key: string]: any
}

// Payment data
interface PaymentData {
  [key: string]: any
}

// Pipeline data
interface PipelineData {
  [key: string]: any
}

// Delivery data
interface DeliveryData {
  [key: string]: any
}

// Installation data
interface InstallationData {
  [key: string]: any
}

// Change order - structured tracking for order modifications
export interface ChangeOrder {
  id: string                   // "CO-1", "CO-2", etc.
  version: number              // 1, 2, 3...
  reason: string               // Why the change was needed
  deltaCutlist: DeltaCutlistPiece[]  // New/modified pieces only
  costDelta: number            // Client pays this additional amount
  payoutDelta: number          // Partner receives this additional amount
  status: 'pending_approval' | 'approved' | 'invoiced' | 'paid' | 'released' | 'rejected'
  createdAt: string
  createdBy: string            // Designer who created the change order
  approvedBy?: string          // Admin/Finance who approved
  approvedAt?: string
  invoiceId?: string           // Stripe/invoice ID for payment tracking
  paidAt?: string              // When client paid for change order
  releasedAt?: string          // When sent back to partner
  releasedToPartnerId?: string // Which partner received it
  releasedToPartnerName?: string
  rushOrder: boolean           // Priority flag
  notes?: string               // Additional notes
}

// Delta cutlist piece for change orders
export interface DeltaCutlistPiece {
  id: string
  name: string
  quantity: number
  dimensions: {
    length?: number | string
    width?: number | string
    thickness?: number | string
  }
  material?: string
  notes?: string
  isReplacement?: boolean      // True if replacing existing piece
  replacesOriginalId?: string  // ID of piece being replaced
}

// Message
interface Message {
  [key: string]: any
}

// Commission data
interface CommissionData {
  [key: string]: any
}

// Note
interface Note {
  [key: string]: any
}

// Documents data
interface DocumentsData {
  [key: string]: any
}

// Timeline entry
interface TimelineEntry {
  [key: string]: any
}

// Main Order interface
export interface Order {
  id?: string | number
  orderId: string
  orderDate: string
  client: ClientData
  config: ConfigData
  wood: string
  finish: string
  addOns: AddOn[]
  beds: Bed[]
  pricing: PricingData
  payment: PaymentData
  status: string
  pipelineStage: string
  pipeline: PipelineData
  productionStage: string
  fabricationStatus: string
  requestedBy: string
  assignedDesigner: string
  assignedPartnerId: string | null
  assignedPartnerName: string | null
  claimedByPartner: string | null
  claimedByPartnerName: string | null
  delivery: DeliveryData
  installation: InstallationData
  changeOrders: ChangeOrder[]
  messages: Message[]
  invoiceNumber: string | null
  invoiceSentAt: string | null
  commission: CommissionData
  dueDate: string | null
  installTentativeDate: string | null
  installConfirmedDate: string | null
  installRescheduleDates: any[]
  installNotes: string | null
  notes: Note[]
  documents: DocumentsData
  timeline: TimelineEntry[]
  createdAt: string
  updatedAt: string
  createdBy: string
}

// Supabase row format (database format)
interface SupabaseOrder {
  id?: string | number
  order_id: string
  order_date: string
  client: ClientData
  config: ConfigData
  wood: string
  finish: string
  add_ons: AddOn[]
  beds: Bed[]
  pricing: PricingData
  payment: PaymentData
  status: string
  pipeline_stage: string
  pipeline: PipelineData
  production_stage: string
  fabrication_status: string
  requested_by: string
  assigned_designer: string
  assigned_partner_id: string | null
  assigned_partner_name: string | null
  claimed_by_partner: string | null
  claimed_by_partner_name: string | null
  delivery: DeliveryData
  installation: InstallationData
  change_orders: ChangeOrder[]
  messages: Message[]
  invoice_number: string | null
  invoice_sent_at: string | null
  commission: CommissionData
  due_date: string | null
  install_tentative_date: string | null
  install_confirmed_date: string | null
  install_reschedule_dates: any[]
  install_notes: string | null
  notes: Note[]
  documents: DocumentsData
  timeline: TimelineEntry[]
  created_at: string
  updated_at: string
  created_by: string
}

// Response types
interface SuccessResponse<T> {
  data: T
  error: null
}

interface ErrorResponse {
  data: null
  error: SupabaseError | any
}

interface DeleteResponse {
  success: boolean
  error: SupabaseError | any | null
}

interface MigrationResponse {
  success: boolean
  migrated?: number
  errors?: Array<{ orderId: string; error: any }>
  error?: string
}

interface SubscriptionPayload {
  event: string
  order: Order | null
  oldOrder: Order | null
}

type RealtimeChannel = any // Supabase RealtimeChannel type

// Check if Supabase is configured
const isSupabaseConfigured = (): boolean => {
  const url = import.meta.env.VITE_SUPABASE_URL
  const key = import.meta.env.VITE_SUPABASE_ANON_KEY
  return url && key && url !== 'https://your-project-id.supabase.co'
}

// =============================================================================
// ORDER CRUD OPERATIONS
// =============================================================================

/**
 * Get all orders from Supabase
 */
export async function getAllOrdersSupabase(): Promise<SuccessResponse<Order[] | null> | ErrorResponse> {
  if (!isSupabaseConfigured()) {
    log.warn('Supabase not configured, using localStorage')
    return { data: null, error: { message: 'Supabase not configured' } }
  }

  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .order('created_at', { ascending: false })

  if (error) {
    log.error('Error fetching orders:', error)
    return { data: null, error }
  }

  // Transform Supabase format to app format
  const orders = (data as SupabaseOrder[]).map(transformFromSupabase)
  return { data: orders, error: null }
}

/**
 * Get a single order by ID
 * Supports multiple ID formats: order_id (case-insensitive)
 */
export async function getOrderByIdSupabase(orderId: string): Promise<SuccessResponse<Order | null> | ErrorResponse> {
  if (!isSupabaseConfigured()) {
    log.warn('Supabase not configured for order lookup')
    return { data: null, error: { message: 'Supabase not configured' } }
  }

  const searchId = orderId?.trim()
  log.info('Searching Supabase for order:', searchId)

  // Try order_id with case-insensitive match
  let { data, error } = await supabase
    .from('orders')
    .select('*')
    .ilike('order_id', searchId)
    .maybeSingle()

  log.info('Supabase order_id search result:', { found: !!data, error: error?.message })

  // If not found, try exact match (in case ilike doesn't work)
  if (!data && !error) {
    const result = await supabase
      .from('orders')
      .select('*')
      .eq('order_id', searchId)
      .maybeSingle()
    data = result.data as SupabaseOrder | null
    error = result.error
    log.info('Supabase exact match result:', { found: !!data, error: error?.message })
  }

  // Also try uppercase version
  if (!data && !error) {
    const result = await supabase
      .from('orders')
      .select('*')
      .eq('order_id', searchId.toUpperCase())
      .maybeSingle()
    data = result.data as SupabaseOrder | null
    error = result.error
    log.info('Supabase uppercase match result:', { found: !!data, error: error?.message })
  }

  if (error) {
    log.error('Error fetching order from Supabase:', error)
    return { data: null, error }
  }

  if (!data) {
    log.info('Order not found in Supabase:', searchId)
    return { data: null, error: null }
  }

  log.info('Order found in Supabase:', data.order_id)
  return { data: transformFromSupabase(data), error: null }
}

/**
 * Create a new order
 */
export async function createOrderSupabase(orderData: Partial<Order>): Promise<SuccessResponse<Order | null> | ErrorResponse> {
  if (!isSupabaseConfigured()) {
    return { data: null, error: { message: 'Supabase not configured' } }
  }

  const supabaseOrder = transformToSupabase(orderData)
  const tenant_id = await requireTenantId()

  const { data, error } = await supabase
    .from('orders')
    .insert({ ...supabaseOrder, tenant_id })
    .select()
    .maybeSingle()

  if (error) {
    log.error('Error creating order:', error)
    return { data: null, error }
  }

  return { data: transformFromSupabase(data as SupabaseOrder), error: null }
}

/**
 * Update an existing order - uses UPSERT to create if doesn't exist
 * This fixes the issue where orders created in localStorage were never synced to Supabase
 */
export async function updateOrderSupabase(orderId: string, updates: Partial<Order>): Promise<SuccessResponse<Order | null> | ErrorResponse> {
  if (!isSupabaseConfigured()) {
    return { data: null, error: { message: 'Supabase not configured' } }
  }

  const supabaseUpdates = transformToSupabase(updates)
  const now = new Date().toISOString()
  ;(supabaseUpdates as any).updated_at = now
  
  // ==========================================================================
  // DATA INTEGRITY SAFEGUARD: BLOCK when explicitly setting claimed without partner fields
  // This prevents the bug where Design Studio shows "Claimed" but Millworks Portal shows nothing
  // ==========================================================================
  const fabStatus = (supabaseUpdates as any).fabrication_status
  const hasPartnerName = !!(supabaseUpdates as any).claimed_by_partner_name
  const hasPartnerLocation = !!(supabaseUpdates as any).claimed_by_partner_location
  const hasPartnerUUID = !!(supabaseUpdates as any).claimed_by_partner
  
  // Check if update is explicitly trying to set partner fields (not just a partial update)
  const updatesAny = updates as any
  const isSettingPartnerFields = 'claimedByPartner' in updatesAny || 
                                  'claimedByPartnerName' in updatesAny || 
                                  'claimedByPartnerLocation' in updatesAny
  
  // BLOCK when explicitly setting claimed + partner fields but all partner fields are empty
  // Partial updates (not touching partner fields) are allowed to proceed
  if (fabStatus === 'claimed' && isSettingPartnerFields && !hasPartnerName && !hasPartnerLocation && !hasPartnerUUID) {
    log.error('🚨 DATA INTEGRITY BLOCK: Attempted to set fabrication_status=claimed with empty partner fields', {
      orderId,
      fabStatus,
      isSettingPartnerFields,
      updates
    })
    return { 
      data: null, 
      error: { 
        message: 'Cannot set fabrication_status=claimed without partner identification (claimedByPartnerName or claimedByPartnerLocation required)' 
      } 
    }
  }
  
  // CRITICAL: Include created_at for inserts (upsert may create new row)
  // Supabase may have NOT NULL constraint on created_at without a default
  if (!(supabaseUpdates as any).created_at) {
    (supabaseUpdates as any).created_at = now
  }

  // CRITICAL FIX: Use UPSERT instead of UPDATE
  // This ensures orders get created in Supabase if they only existed in localStorage
  // Without this, claims and status updates silently fail because there's no row to update
  
  // CRITICAL: Strip base64 image data to prevent PostgreSQL timeout (error 57014)
  const strippedUpdates = stripImageData(supabaseUpdates)
  
  // DEBUG: Log exactly what we're sending to Supabase
  console.log('🔍 UPSERT DEBUG - orderId:', orderId)
  console.log('🔍 UPSERT DEBUG - fabrication_status:', strippedUpdates.fabrication_status)
  console.log('🔍 UPSERT DEBUG - keys being sent:', Object.keys(strippedUpdates))
  
  // FIX: Use .maybeSingle() instead of .maybeSingle() to avoid 406 errors
  // .maybeSingle() throws 406 when 0 rows returned, .maybeSingle() returns null gracefully
  const { data, error } = await supabase
    .from('orders')
    .upsert(
      { 
        order_id: orderId,
        ...strippedUpdates 
      }, 
      { 
        onConflict: 'order_id',
        ignoreDuplicates: false // Update if exists
      }
    )
    .select()
    .maybeSingle()

  if (error) {
    // Log detailed error info for debugging
    console.error('❌ UPSERT FAILED:', orderId, error.code, error.message)
    log.error('Error upserting order:', { 
      orderId,
      errorCode: error.code,
      errorMessage: error.message,
      errorDetails: error.details,
      errorHint: error.hint
    })
    return { data: null, error }
  }

  // DEBUG: Log what came back
  console.log('✅ UPSERT SUCCESS:', orderId, 'fab_status:', data?.fabrication_status)
  log.info('Order upserted successfully:', orderId)
  return { data: transformFromSupabase(data as SupabaseOrder), error: null }
}

/**
 * Delete an order
 */
export async function deleteOrderSupabase(orderId: string): Promise<DeleteResponse> {
  if (!isSupabaseConfigured()) {
    return { success: false, error: { message: 'Supabase not configured' } }
  }

  const { error } = await supabase
    .from('orders')
    .delete()
    .eq('order_id', orderId)

  if (error) {
    log.error('Error deleting order:', error)
    return { success: false, error }
  }

  return { success: true, error: null }
}

// =============================================================================
// QUERY HELPERS
// =============================================================================

/**
 * Get orders by status
 */
export async function getOrdersByStatusSupabase(status: string): Promise<SuccessResponse<Order[] | null> | ErrorResponse> {
  if (!isSupabaseConfigured()) {
    return { data: null, error: { message: 'Supabase not configured' } }
  }

  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .eq('status', status)
    .order('created_at', { ascending: false })

  if (error) {
    return { data: null, error }
  }

  return { data: (data as SupabaseOrder[]).map(transformFromSupabase), error: null }
}

/**
 * Get orders by production stage
 */
export async function getOrdersByStageSupabase(stage: string): Promise<SuccessResponse<Order[] | null> | ErrorResponse> {
  if (!isSupabaseConfigured()) {
    return { data: null, error: { message: 'Supabase not configured' } }
  }

  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .eq('production_stage', stage)
    .order('created_at', { ascending: false })

  if (error) {
    return { data: null, error }
  }

  return { data: (data as SupabaseOrder[]).map(transformFromSupabase), error: null }
}

/**
 * Get orders claimed by a specific partner
 * Checks both claimed_by_partner (UUID) and claimed_by_partner_location (slug)
 * because partner IDs can be either UUIDs or slugs like "choctaw-beach"
 */
export async function getOrdersByPartnerSupabase(partnerId: string): Promise<SuccessResponse<Order[] | null> | ErrorResponse> {
  if (!isSupabaseConfigured()) {
    return { data: null, error: { message: 'Supabase not configured' } }
  }

  // Check if partnerId is a valid UUID
  const isValidUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(partnerId);

  // Query both claimed_by_partner (UUID) and claimed_by_partner_location (slug)
  // For slug-based IDs, the data is stored in claimed_by_partner_location
  const { data, error } = await supabase
    .from('orders')
    .select('*')
    .or(`claimed_by_partner.eq.${partnerId},claimed_by_partner_location.eq.${partnerId}`)
    .order('created_at', { ascending: false })

  if (error) {
    return { data: null, error }
  }

  return { data: (data as SupabaseOrder[]).map(transformFromSupabase), error: null }
}

// =============================================================================
// REAL-TIME SUBSCRIPTIONS
// =============================================================================

/**
 * Subscribe to order changes
 */
export function subscribeToOrders(callback: (payload: SubscriptionPayload) => void): RealtimeChannel | null {
  if (!isSupabaseConfigured()) {
    log.warn('Supabase not configured, real-time disabled')
    return null
  }

  const channel = supabase
    .channel('orders_changes')
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'orders'
      },
      (payload: any) => {
        const event = payload.eventType
        const order = payload.new ? transformFromSupabase(payload.new as SupabaseOrder) : null
        const oldOrder = payload.old ? transformFromSupabase(payload.old as SupabaseOrder) : null
        callback({ event, order, oldOrder })
      }
    )
    .subscribe()

  return channel
}

/**
 * Unsubscribe from order changes
 */
export function unsubscribeFromOrders(channel: RealtimeChannel): void {
  if (channel) {
    supabase.removeChannel(channel)
  }
}

// =============================================================================
// DATA TRANSFORMATION
// Uses unified OrderTransformService for consistency across codebase
// =============================================================================

/**
 * Transform Supabase row to app format
 * DELEGATES to OrderTransformService.transformOrderFromSupabase for single source of truth
 */
function transformFromSupabase(row: SupabaseOrder | null): Order | null {
  return transformOrderFromSupabase(row) as Order | null
}

/**
 * Transform app format to Supabase row
 */
function transformToSupabase(order: Partial<Order>): Record<string, any> {
  const row: Record<string, any> = {}

  // Core order fields
  if (order.orderId !== undefined) row.order_id = order.orderId
  if (order.orderDate !== undefined) row.order_date = order.orderDate
  if (order.client !== undefined) row.client = order.client
  if (order.config !== undefined) row.config = order.config
  if (order.wood !== undefined) row.wood = order.wood
  if (order.finish !== undefined) row.finish = order.finish
  if (order.addOns !== undefined) row.add_ons = order.addOns
  if (order.beds !== undefined) row.beds = order.beds
  if (order.pricing !== undefined) row.pricing = order.pricing
  if (order.payment !== undefined) row.payment = order.payment
  if (order.status !== undefined) row.status = order.status
  if (order.pipelineStage !== undefined) row.pipeline_stage = order.pipelineStage
  if (order.pipeline !== undefined) row.pipeline = order.pipeline
  if (order.productionStage !== undefined) row.production_stage = order.productionStage
  if (order.fabricationStatus !== undefined) row.fabrication_status = order.fabricationStatus
  if (order.requestedBy !== undefined) row.requested_by = order.requestedBy
  if (order.assignedDesigner !== undefined) row.assigned_designer = order.assignedDesigner
  if (order.assignedPartnerId !== undefined) row.assigned_partner_id = order.assignedPartnerId
  if (order.assignedPartnerName !== undefined) row.assigned_partner_name = order.assignedPartnerName
  // CRITICAL: claimed_by_partner has FK constraint to partners table (UUID PKs)
  // Never write non-UUID values (e.g. slugs like "choctaw-beach") — use null instead
  if (order.claimedByPartner !== undefined) {
    const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(order.claimedByPartner || '')
    row.claimed_by_partner = isUUID ? order.claimedByPartner : null
  }
  if (order.claimedByPartnerName !== undefined) row.claimed_by_partner_name = order.claimedByPartnerName
  if (order.delivery !== undefined) row.delivery = order.delivery
  if (order.installation !== undefined) row.installation = order.installation
  if (order.changeOrders !== undefined) row.change_orders = order.changeOrders
  if (order.messages !== undefined) row.messages = order.messages
  if (order.invoiceNumber !== undefined) row.invoice_number = order.invoiceNumber
  if (order.invoiceSentAt !== undefined) row.invoice_sent_at = order.invoiceSentAt
  if (order.commission !== undefined) row.commission = order.commission
  if (order.dueDate !== undefined) row.due_date = order.dueDate
  if (order.notes !== undefined) row.notes = order.notes
  if (order.documents !== undefined) row.documents = order.documents
  if (order.timeline !== undefined) row.timeline = order.timeline
  if (order.createdBy !== undefined) row.created_by = order.createdBy

  // ==========================================================================
  // RELEASE TO PRODUCTION FIELDS (Migration 040)
  // CRITICAL: These were missing and causing orders to not appear in Millworks Portal
  // ==========================================================================
  if ((order as any).releasedToProduction !== undefined) row.released_to_production = (order as any).releasedToProduction
  if ((order as any).releasedToProductionAt !== undefined) row.released_to_production_at = (order as any).releasedToProductionAt
  if ((order as any).releasedToProductionBy !== undefined) row.released_to_production_by = (order as any).releasedToProductionBy
  if ((order as any).productionVersion !== undefined) row.production_version = (order as any).productionVersion
  if ((order as any).sentToProductionAt !== undefined) row.sent_to_production_at = (order as any).sentToProductionAt
  if ((order as any).productionHistory !== undefined) row.production_history = (order as any).productionHistory

  // ==========================================================================
  // PARTNER/FABRICATION FIELDS (Migration 012, 018)
  // ==========================================================================
  if ((order as any).claimedByPartnerAt !== undefined) row.claimed_by_partner_at = (order as any).claimedByPartnerAt
  if ((order as any).claimedByPartnerLocation !== undefined) row.claimed_by_partner_location = (order as any).claimedByPartnerLocation
  if ((order as any).fabPayout !== undefined) row.fab_payout = (order as any).fabPayout
  if ((order as any).fabPayoutAmount !== undefined) row.fab_payout = (order as any).fabPayoutAmount
  // VERIFIED: These columns exist in Supabase schema
  if ((order as any).releaseMode !== undefined) row.release_mode = (order as any).releaseMode
  if ((order as any).lockedSpecs !== undefined) row.locked_specs = (order as any).lockedSpecs
  if ((order as any).bids !== undefined) row.bids = (order as any).bids
  if ((order as any).biddingEndsAt !== undefined) row.bidding_ends_at = (order as any).biddingEndsAt
  // NOT IN SCHEMA: winning_bid, recall_reason, scrap_allocations, admin_actions

  // Target partner fields (direct assignment)
  if ((order as any).targetPartnerId !== undefined) row.target_partner_id = (order as any).targetPartnerId
  if ((order as any).targetPartnerName !== undefined) row.target_partner_name = (order as any).targetPartnerName
  if ((order as any).releasedToPartnersAt !== undefined) row.released_to_partners_at = (order as any).releasedToPartnersAt

  // ==========================================================================
  // PARTNER WORKFLOW FIELDS - VERIFIED: All columns exist in schema
  // ==========================================================================
  if ((order as any).workStartedAt !== undefined) row.work_started_at = (order as any).workStartedAt
  if ((order as any).pausedAt !== undefined) row.paused_at = (order as any).pausedAt
  if ((order as any).pauseReason !== undefined) row.pause_reason = (order as any).pauseReason
  if ((order as any).statusBeforePause !== undefined) row.status_before_pause = (order as any).statusBeforePause
  if ((order as any).resumedAt !== undefined) row.resumed_at = (order as any).resumedAt
  if ((order as any).returnedByPartner !== undefined) row.returned_by_partner = (order as any).returnedByPartner
  if ((order as any).returnedByPartnerName !== undefined) row.returned_by_partner_name = (order as any).returnedByPartnerName
  if ((order as any).returnedAt !== undefined) row.returned_at = (order as any).returnedAt
  if ((order as any).returnReason !== undefined) row.return_reason = (order as any).returnReason
  if ((order as any).partnerMessages !== undefined) row.partner_messages = (order as any).partnerMessages
  if ((order as any).hasUnreadPartnerQuestion !== undefined) row.has_unread_partner_question = (order as any).hasUnreadPartnerQuestion
  if ((order as any).lastPartnerQuestionAt !== undefined) row.last_partner_question_at = (order as any).lastPartnerQuestionAt

  // ==========================================================================
  // DESIGN DATA FIELDS - VERIFIED: All columns exist in schema
  // ==========================================================================
  if ((order as any).roomDimensions !== undefined) row.room_dimensions = (order as any).roomDimensions
  if ((order as any).woodType !== undefined) row.wood_type = (order as any).woodType
  if ((order as any).renders !== undefined) row.renders = (order as any).renders
  if ((order as any).floorPlanCapture !== undefined) row.floor_plan_capture = (order as any).floorPlanCapture
  if ((order as any).addOns !== undefined) row.add_ons = (order as any).addOns

  // ==========================================================================
  // CUTLIST & WORKFLOW FIELDS (DesignStudio)
  // ==========================================================================
  if ((order as any).cutlistData !== undefined) row.cutlist_data = (order as any).cutlistData
  if ((order as any).workflowSteps !== undefined) row.workflow_steps = (order as any).workflowSteps

  // ==========================================================================
  // CLIENT SIGNATURE & DESIGN APPROVAL (Permanent Record)
  // ==========================================================================
  if ((order as any).clientSignature !== undefined) row.client_signature = (order as any).clientSignature
  if ((order as any).designApprovedAt !== undefined) row.design_approved_at = (order as any).designApprovedAt
  if ((order as any).designApprovedBy !== undefined) row.design_approved_by = (order as any).designApprovedBy
  if ((order as any).approvalStatus !== undefined) row.approval_status = (order as any).approvalStatus

  // ==========================================================================
  // SIDEBAR STATE FIELDS (Migration 041)
  // ==========================================================================
  if ((order as any).deliveryZone !== undefined) row.delivery_zone = (order as any).deliveryZone
  if ((order as any).wantsInstall !== undefined) row.wants_install = (order as any).wantsInstall
  if ((order as any).discount !== undefined) row.discount = (order as any).discount
  if ((order as any).guardrailStyle !== undefined) row.guardrail_style = (order as any).guardrailStyle
  if ((order as any).guardrailCoverage !== undefined) row.guardrail_coverage = (order as any).guardrailCoverage
  if ((order as any).guardrailConfigs !== undefined) row.guardrail_configs = (order as any).guardrailConfigs
  if ((order as any).guardrailTemplate !== undefined) row.guardrail_template = (order as any).guardrailTemplate

  // ==========================================================================
  // JOB COSTING FIELDS (Budget allocation persistence fix)
  // ==========================================================================
  if ((order as any).budgetAllocation !== undefined) row.budget_allocation = (order as any).budgetAllocation
  if ((order as any).budgetActuals !== undefined) row.budget_actuals = (order as any).budgetActuals
  if ((order as any).materialActuals !== undefined) row.material_actuals = (order as any).materialActuals
  if ((order as any).shippingActuals !== undefined) row.shipping_actuals = (order as any).shippingActuals
  if ((order as any).jobCosting !== undefined) row.job_costing = (order as any).jobCosting

  return row
}

// =============================================================================
// MIGRATION HELPER
// =============================================================================

/**
 * Migrate orders from localStorage to Supabase
 * DEPRECATED: This is a one-time migration utility
 * New orders should go directly to Supabase via OrdersService
 */
export async function migrateFromLocalStorage(): Promise<MigrationResponse> {
  // DEPRECATED: Migration complete - Supabase is now source of truth
  console.warn('[DEPRECATED] migrateFromLocalStorage - migration already complete')
  return { success: true, migrated: 0 }
}

// =============================================================================
// TRACKING STATUS - Update order tracking status in Supabase
// =============================================================================

/**
 * Update tracking status for an order
 * CRITICAL: This MERGES with existing tracking_status, not replaces
 * This prevents losing previous tracking steps when fabrication updates
 */
export async function updateTrackingStatus(
  orderId: string, 
  trackingStatusUpdate: any
): Promise<{ success: boolean; error?: string; orderExists?: boolean }> {
  if (!isSupabaseConfigured()) {
    return { success: false, error: 'Supabase not configured' }
  }

  // Normalize order ID - try multiple formats
  const normalizedId = orderId?.trim()
  const upperCaseId = normalizedId?.toUpperCase()

  try {
    log.info(`Updating tracking status for order: ${normalizedId}`)
    
    // FIX 2: First check if order exists and get current tracking_status
    const { data: existingOrder, error: fetchError } = await supabase
      .from('orders')
      .select('order_id, tracking_status')
      .or(`order_id.eq.${normalizedId},order_id.ilike.${normalizedId},order_id.eq.${upperCaseId}`)
      .maybeSingle()

    if (fetchError) {
      log.error('Error fetching order for tracking update:', fetchError)
      return { success: false, error: fetchError.message, orderExists: false }
    }

    if (!existingOrder) {
      log.warn(`Order not found in Supabase for tracking update: ${normalizedId}`)
      return { success: false, error: 'Order not found in Supabase', orderExists: false }
    }

    // FIX 1: MERGE existing tracking_status with new updates (not replace)
    const existingStatus = existingOrder.tracking_status || {}
    const mergedStatus = {
      ...existingStatus,
      ...trackingStatusUpdate,
      // Deep merge fabricationProgress if both exist
      fabricationProgress: {
        ...(existingStatus.fabricationProgress || {}),
        ...(trackingStatusUpdate.fabricationProgress || {})
      }
    }
    
    const { error } = await supabase
      .from('orders')
      .update({ 
        tracking_status: mergedStatus,
        updated_at: new Date().toISOString()
      })
      .eq('order_id', existingOrder.order_id) // Use the exact ID from DB

    if (error) {
      log.error('Error updating tracking status:', error)
      return { success: false, error: error.message, orderExists: true }
    }

    log.info(`✅ Tracking status MERGED for order: ${existingOrder.order_id}`)
    return { success: true, orderExists: true }
  } catch (err) {
    log.error('updateTrackingStatus error:', err)
    return { success: false, error: (err as Error).message }
  }
}

/**
 * Get fabrication queue - orders ahead of a given order
 */
export async function getFabricationQueue(): Promise<{ data: any[]; error?: string }> {
  if (!isSupabaseConfigured()) {
    return { data: [], error: 'Supabase not configured' }
  }

  try {
    // Get all orders in fabrication queue, ordered by queue entry date
    const { data, error } = await supabase
      .from('orders')
      .select('order_id, client, tracking_status, created_at')
      .or('tracking_status->>currentPhase.eq.pre_production,tracking_status->>currentPhase.eq.production')
      .order('created_at', { ascending: true })

    if (error) {
      log.error('Error fetching fabrication queue:', error)
      return { data: [], error: error.message }
    }

    return { data: data || [] }
  } catch (err) {
    log.error('getFabricationQueue error:', err)
    return { data: [], error: (err as Error).message }
  }
}

// =============================================================================
// ATOMIC CLAIM - Race-safe order claiming with database-level guard
// Uses .update().eq('fabrication_status', 'ready') so only the first partner wins
// =============================================================================

export interface ClaimPayload {
  orderId: string
  fabricationStatus: string
  claimedByPartner?: string
  claimedByPartnerName: string
  claimedByPartnerLocation: string
  claimedByPartnerAt: string
  targetPartnerId?: string | null
  targetPartnerName?: string | null
  releasedToPartnersAt?: string | null
  fabPayout?: number | null
  updatedAt: string
}

export interface ClaimResult {
  success: boolean
  data: Order | null
  error?: string
}

export async function claimOrderAtomic(
  orderId: string,
  claimFields: ClaimPayload
): Promise<ClaimResult> {
  if (!isSupabaseConfigured()) {
    return { success: false, data: null, error: 'Supabase not configured' }
  }

  const supabaseFields: Record<string, unknown> = {
    ...transformToSupabase(claimFields as unknown as Partial<Order>),
    updated_at: new Date().toISOString(),
    fabrication_status: 'claimed'
  }

  // ATOMIC: Only update if fabrication_status is still 'ready'
  // If another partner claimed first, this returns 0 rows → null data
  const { data, error } = await supabase
    .from('orders')
    .update(supabaseFields)
    .eq('order_id', orderId)
    .eq('fabrication_status', 'ready')
    .select()
    .maybeSingle()

  if (error) {
    log.error('[claimOrderAtomic] Supabase error:', { orderId, error })
    return { success: false, data: null, error: error.message }
  }

  if (!data) {
    log.warn('[claimOrderAtomic] No rows updated — order already claimed or not found:', orderId)
    return { success: false, data: null, error: 'Order was already claimed by another partner or is no longer available' }
  }

  const row = data as SupabaseOrder
  const transformed = transformFromSupabase(row)
  log.info('[claimOrderAtomic] ✅ Atomic claim SUCCESS:', {
    orderId,
    fabricationStatus: row.fabrication_status,
    claimedByPartner: row.claimed_by_partner,
    claimedByPartnerName: row.claimed_by_partner_name
  })

  return { success: true, data: transformed }
}

export default {
  getAllOrders: getAllOrdersSupabase,
  getOrderById: getOrderByIdSupabase,
  createOrder: createOrderSupabase,
  updateOrder: updateOrderSupabase,
  deleteOrder: deleteOrderSupabase,
  getOrdersByStatus: getOrdersByStatusSupabase,
  getOrdersByStage: getOrdersByStageSupabase,
  getOrdersByPartner: getOrdersByPartnerSupabase,
  subscribeToOrders,
  unsubscribeFromOrders,
  migrateFromLocalStorage,
  isSupabaseConfigured,
  updateTrackingStatus,
  getFabricationQueue,
  claimOrderAtomic
}
